import React, { useState, useRef } from 'react';
import { Upload, Loader2, X, Image as ImageIcon, Plus, Star } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * MultiImageUploader - upload multiple photos from device gallery (JPG/PNG).
 * The first image is marked as "Principal" (cover).
 *
 * Props:
 *   value: array of image URLs
 *   onChange: (urls: string[]) => void
 *   folder: cloudinary folder
 */
const MultiImageUploader = ({ value = [], onChange, folder = 'tts/general' }) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileInputRef = useRef(null);
  const images = Array.isArray(value) ? value : [];

  const uploadOne = async (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(`${file.name}: solo JPG o PNG`);
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`${file.name}: máx 10MB`);
      return null;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    try {
      const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return res.data.url;
    } catch (err) {
      toast.error(`Error al subir ${file.name}`);
      return null;
    }
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setUploadingCount(arr.length);
    const newUrls = [];
    for (const f of arr) {
      const url = await uploadOne(f);
      if (url) newUrls.push(url);
      setUploadingCount(c => Math.max(0, c - 1));
    }
    if (newUrls.length) {
      onChange([...images, ...newUrls]);
      toast.success(`${newUrls.length} imagen(es) subida(s)`);
    }
    setUploadingCount(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAt = (idx) => {
    const arr = [...images];
    arr.splice(idx, 1);
    onChange(arr);
  };

  const setAsCover = (idx) => {
    if (idx === 0) return;
    const arr = [...images];
    const [item] = arr.splice(idx, 1);
    arr.unshift(item);
    onChange(arr);
    toast.success('Imagen principal actualizada');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {images.length === 0 ? (
        <div
          onClick={() => uploadingCount === 0 && fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="border-2 border-dashed border-white/15 hover:border-[#ff6b1a]/60 rounded-lg p-6 text-center cursor-pointer transition-colors bg-black/20"
        >
          {uploadingCount > 0 ? (
            <div className="flex flex-col items-center gap-2 py-3">
              <Loader2 className="w-7 h-7 animate-spin text-[#ff6b1a]" />
              <span className="text-sm text-white/70">Subiendo {uploadingCount}...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-3">
              <div className="w-12 h-12 rounded-full bg-[#ff6b1a]/15 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-[#ff6b1a]" />
              </div>
              <div className="text-sm font-semibold text-white">Subir fotos desde galería</div>
              <div className="text-xs text-white/50">Puedes seleccionar varias · JPG o PNG · máx 10MB c/u</div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((url, idx) => (
              <div key={url} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-[4/3]">
                <img src={url} alt={`img-${idx}`} className="w-full h-full object-cover" />
                {idx === 0 && (
                  <div className="absolute top-1.5 left-1.5 bg-[#ff6b1a] text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1">
                    <Star className="w-3 h-3" fill="white" /> Principal
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {idx !== 0 && (
                    <button type="button" onClick={() => setAsCover(idx)} title="Establecer como principal" className="px-2 py-1 rounded bg-[#ff6b1a] text-white text-xs font-semibold hover:brightness-110">
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button type="button" onClick={() => removeAt(idx)} title="Eliminar" className="px-2 py-1 rounded bg-red-600 text-white text-xs font-semibold hover:brightness-110">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

            {/* Add more tile */}
            <button
              type="button"
              onClick={() => uploadingCount === 0 && fileInputRef.current?.click()}
              disabled={uploadingCount > 0}
              className="aspect-[4/3] rounded-lg border-2 border-dashed border-white/15 hover:border-[#ff6b1a]/60 bg-black/20 flex flex-col items-center justify-center gap-1 text-white/60 hover:text-[#ff6b1a] transition-colors"
            >
              {uploadingCount > 0 ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin text-[#ff6b1a]" />
                  <span className="text-[11px]">Subiendo {uploadingCount}...</span>
                </>
              ) : (
                <>
                  <Plus className="w-6 h-6" />
                  <span className="text-[11px] font-semibold">Agregar más</span>
                </>
              )}
            </button>
          </div>

          <div className="text-xs text-white/40 mt-2">
            {images.length} foto{images.length !== 1 ? 's' : ''} · La primera es la imagen principal
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiImageUploader;
